<template>
    <div class="users-permissions d-flex flex-column fill-height">
        <div class="toolbar flex-shrink-0">
            <v-row no-gutters class="white">
                <v-col cols="3" lg="3" md="4" sm="12" class="pr-2">
                    <div class="d-flex align-center ml-2">
                        <v-autocomplete
                            v-model="userToFilter"
                            flat
                            :items="users"
                            item-text="name"
                            item-value="name"
                            label="Search"
                            prefix="User :   "
                            hide-details
                            class="flex-grow-1"
                        />
                        <v-chip
                            small
                            class="ml-2 mt-3 flex-shrink-0"
                            color="primary"
                            outlined
                            style="font-size: 14px;"
                        >
                            {{ filteredUsersCount }}
                            {{ filteredUsersCount === 1 ? 'user' : 'users' }}
                        </v-chip>
                    </div>
                </v-col>
                <v-col cols="4" lg="4" md="4" sm="12" class="pl-2">
                    <div class="d-flex align-center ml-2">
                        <v-autocomplete
                            v-model="permissionToFilter"
                            flat
                            :items="headers"
                            item-text="permissionName"
                            item-value="permissionString"
                            label="Search"
                            prefix="Permission :   "
                            hide-details
                            class="flex-grow-1"
                        />
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    class="ml-2 mt-3 flex-shrink-0"
                                    color="primary"
                                    outlined
                                    style="font-size: 14px;"
                                >
                                    {{ totalPermissionsCount }}
                                    {{
                                        totalPermissionsCount === 1
                                            ? 'permission'
                                            : 'permissions'
                                    }}
                                </v-chip>
                            </template>
                            <div
                                v-for="title in permissionsTitles"
                                :key="title.label"
                            >
                                {{ capitalizeFirst(title.label) }}:
                                {{ title.items }}
                            </div>
                        </v-tooltip>
                    </div>
                </v-col>
                <v-spacer />
                <v-btn
                    class="ml-6 mt-2"
                    rounded
                    elevation="0"
                    @click="downloadMatrix()"
                    :disabled="loading"
                >
                    <v-icon>mdi-download</v-icon> Matrix
                </v-btn>

                <v-btn
                    class="mx-4 mt-2"
                    rounded
                    elevation="0"
                    :loading="loadingActiveUsers"
                    :disabled="loadingActiveUsers"
                    @click="downloadActive()"
                >
                    <v-icon>mdi-download</v-icon> Auth
                </v-btn>
            </v-row>
        </div>

        <div class="scroll-container flex-grow-1">
            <table class="permissions-table">
                <thead :style="{ 'background-color': 'lightgray' }">
                    <tr>
                        <th
                            :style="{
                                'background-color': '#eee',
                                position: 'sticky',
                                top: 0,
                                'z-index': 2,
                            }"
                        ></th>
                        <th
                            v-for="(title, i) in permissionsTitles"
                            :key="i"
                            :colspan="title.items"
                            class="px-2 pt-2"
                            :style="{
                                'background-color': `${title.color}`,
                                position: 'sticky',
                                top: 0,
                                'z-index': 2,
                            }"
                        >
                            {{ title.text }}
                        </th>
                    </tr>
                    <tr>
                        <td
                            :style="{
                                'background-color': '#eee',
                                position: 'sticky',
                                top: '30px',
                                'z-index': 1,
                            }"
                        >
                            <span id="header-user">User</span>
                        </td>

                        <td
                            v-for="(header, j) in headers"
                            :key="j"
                            :style="{
                                'background-color': `${header.color}`,
                                'justify-items': 'center',
                                'align-items': 'center',
                                position: 'sticky',
                                top: '30px',
                                'z-index': 1,
                            }"
                        >
                            <span id="header">
                                {{ header.value }}
                            </span>
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(user, k) in filteredUsers" :key="k">
                        <td id="info">
                            <span> {{ user.name }}</span>
                        </td>
                        <td v-for="(header, j) in headers" :key="j" id="info">
                            <v-icon
                                small
                                class="pa-2 d-flex justify-center align-center"
                                color="primary"
                                v-if="
                                    user.permissions &&
                                        user.permissions.includes(
                                            header.permissionString
                                        )
                                "
                            >
                                mdi-check
                            </v-icon>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { storage } from '@/services/firebase'
import { mapMutations } from 'vuex'
import API from '@/services/api'
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
    name: 'UsersPermissions',
    data() {
        return {
            folderPictures: 'users_pictures',
            loading: false,
            height: 0,
            userToFilter: null,
            companyId: JSON.parse(localStorage.getItem('company')),
            folderLogo: 'company_logo',
            errorMsg: null,
            headers: [],
            users: [],
            permissionsTitles: [],
            permissionToFilter: null,
            loadingActiveUsers: false,
        }
    },
    computed: {
        filteredUsersCount() {
            return this.filteredUsers.length
        },
        totalPermissionsCount() {
            return this.headers.length
        },
        filteredUsers() {
            let conditions = []
            if (this.userToFilter) {
                conditions.push(this.filterUser)
            }
            if (this.permissionToFilter) {
                conditions.push(this.filterPermission)
            }
            if (conditions.length > 0) {
                return this.users.filter(user => {
                    return conditions.every(condition => {
                        return condition(user)
                    })
                })
            }
            this.sort()
            return this.users
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { users },
            } = await API.getUsers()
            this.users = users
            await this.getSettings()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async downloadActive() {
            try {
                this.loadingActiveUsers = true
                const {
                    data: { users: authUsers },
                } = await API.getAuthUsers()
                const users = (authUsers || []).map(user => ({
                    ...user,
                    creationTime: this.toGmt5(user.creationTime),
                    lastSignInTime: this.toGmt5(user.lastSignInTime),
                    lastRefreshTime: this.toGmt5(user.lastRefreshTime),
                }))
                const Worksheet = XLSX.utils.json_to_sheet(users)
                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, Worksheet, 'Users')
                XLSX.writeFile(wb, `Authentication users(${new Date()}).xlsx`)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingActiveUsers = false
            }
        },
        toGmt5(value) {
            if (!value) return ''
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return value
            // ponytail: fixed UTC-5. Upgrade path: timeZone America/Bogota if the offset must follow a named zone.
            const gmt5 = new Date(date.getTime() - 5 * 60 * 60 * 1000)
            const pad = n => String(n).padStart(2, '0')
            return `${gmt5.getUTCFullYear()}-${pad(
                gmt5.getUTCMonth() + 1
            )}-${pad(gmt5.getUTCDate())} ${pad(gmt5.getUTCHours())}:${pad(
                gmt5.getUTCMinutes()
            )}:${pad(gmt5.getUTCSeconds())}`
        },
        downloadMatrix() {
            if (this.users.length == 0) return
            const permissionNameByKey = this.headers.reduce((acc, header) => {
                acc[header.permissionString] = header.permissionName
                return acc
            }, {})
            let matrix = []
            this.users.forEach(user => {
                let userData = {
                    user: user.name,
                    email: user.email,
                }
                user.permissions?.forEach(permission => {
                    const permissionName = permissionNameByKey[permission]
                    if (permissionName) {
                        userData[permissionName] = 'X'
                    }
                })
                matrix.push(userData)
            })
            let xlsHeaders = ['user', 'email'].concat(
                this.headers.map(a => a.permissionName)
            )
            const Worksheet = XLSX.utils.json_to_sheet(matrix, {
                header: xlsHeaders,
            })
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, Worksheet, 'User permissions')
            XLSX.writeFile(wb, `User permissions(${new Date()}).xlsx`)
        },
        async getSettings() {
            try {
                const {
                    data: { settings },
                } = await API.getSettings()
                const indexLogo = settings.findIndex(x => x.name == 'Company')
                if (settings[indexLogo].generalInfo.image.file) {
                    const path = `${this.companyId}/${this.folderLogo}`
                    const storageRef = storage().ref(path)
                    const profilePicRef = storageRef.child(
                        settings[indexLogo].generalInfo.image.file
                    )
                    settings[
                        indexLogo
                    ].generalInfo.image.imageURL = await profilePicRef.getDownloadURL()
                }

                const permissionsSettings = settings.find(
                    x => x.name == 'Permissions'
                )

                let color = '#e6e6e6'
                Object.keys(permissionsSettings.permissions).forEach(key => {
                    const items = permissionsSettings.permissions[key].length

                    this.permissionsTitles.push({
                        text: key.toUpperCase(),
                        label: key,
                        items,
                        color,
                    })
                    permissionsSettings.permissions[key].forEach(p => {
                        let text = p.name
                        this.headers.push({
                            text,
                            value: text,
                            permissionString: p.permission,
                            permissionName: p.name,
                            color,
                        })
                    })
                    color = color == '#e6e6e6' ? '#eee' : '#e6e6e6'
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        filterUser(user) {
            return (
                user.name &&
                user.name
                    .toLowerCase()
                    .includes(this.userToFilter.toLowerCase())
            )
        },
        filterPermission(user) {
            return (
                user.permissions &&
                user.permissions.includes(this.permissionToFilter)
            )
        },
        capitalizeFirst(text) {
            if (!text) return ''
            return text.charAt(0).toUpperCase() + text.slice(1)
        },
        sort() {
            this.users.sort((a, b) => b.name - a.name)
        },
    },
}
</script>

<style scoped>
.users-permissions {
    height: 94%;
    min-height: 0;
    overflow: hidden;
}

.toolbar {
    background-color: white;
    padding: 8px 8px 0;
}

.scroll-container {
    overflow: auto;
    min-height: 0;
    width: 100%;
}

.permissions-table {
    border-collapse: collapse;
}

#header {
    display: inline-block;
    width: 150px;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    padding: 0px 5px;
}
#header-user {
    display: inline-block;
    width: 150px;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    padding: 0px 5px;
}
#info {
    border: 0.5px solid lightgrey !important;
    width: 120px;
    padding: 0px 10px;
}

.scroll-container::-webkit-scrollbar {
    height: 16px;
}

.scroll-container::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 10px;
}

.scroll-container::-webkit-scrollbar-thumb {
    background-color: #6e6e6e;
    border-radius: 10px;
    border: 2px solid #f0f0f0;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
    background-color: #6e6e6e;
}
</style>
